/**
 * PeopleOverview Component
 * 
 * Displays comprehensive overview of a person including:
 * - Header with profile info and summary boxes
 * - Basic Information
 * - Projects Assigned
 * - Skill Matrix
 * - Weekly Workload Chart
 * - Quick Timesheet Summary
 * - Leave/WOFF Summary
 * 
 * @param {string} peopleId - The ID of the person
 * @param {object} personData - Person information
 */

import { useState } from 'react';
import { Row, Col, ProgressBar, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import './PeopleOverview.css';

const PeopleOverview = ({ peopleId, personData }) => {
    // Mock data - Replace with actual API call
    const overviewData = {
        workload: 85,
        timeLogged: {
            today: 6.5,
            week: 38,
            month: 152
        },
        projectsAssigned: 4,
        reporting: {
            teamLead: { name: 'Jane Smith', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
            manager: { name: 'Sarah Wilson', avatar: 'https://randomuser.me/api/portraits/women/50.jpg' }
        }
    };

    const basicInfo = {
        employeeId: 'EMP-2024-045',
        designation: personData.role || 'Frontend Developer',
        department: personData.department || 'Development',
        email: personData.email || 'john.doe@company.com',
        phone: personData.phone || '+1 (555) 123-4567',
        joiningDate: personData.joiningDate || 'Jan 15, 2020',
        experience: personData.experience || '5 years',
        location: 'New York, USA',
        employmentType: 'Full Time',
        workingHours: '9:00 AM - 6:00 PM'
    };

    const projects = [
        { id: 1, name: 'eCommerce Website', client: 'Netclues Technologies', role: 'Frontend Developer', progress: 75, status: 'In Progress', color: 'primary' },
        { id: 2, name: 'Mobile App Development', client: 'Tech Solutions Inc', role: 'Developer', progress: 45, status: 'In Progress', color: 'warning' },
        { id: 3, name: 'CRM System', client: 'Business Corp', role: 'Lead Developer', progress: 90, status: 'Nearly Complete', color: 'success' },
        { id: 4, name: 'Dashboard Analytics', client: 'Data Insights', role: 'Developer', progress: 30, status: 'Just Started', color: 'info' }
    ];

    const skills = [
        { name: 'React.js', level: 90, color: '#61dafb', icon: 'fab fa-react' },
        { name: 'JavaScript', level: 85, color: '#f7df1e', icon: 'fab fa-js' },
        { name: 'HTML/CSS', level: 95, color: '#e34c26', icon: 'fab fa-html5' },
        { name: 'Node.js', level: 75, color: '#68a063', icon: 'fab fa-node-js' },
        { name: 'TypeScript', level: 70, color: '#3178c6', icon: 'fas fa-code' },
        { name: 'Git', level: 80, color: '#f05032', icon: 'fab fa-git-alt' }
    ];

    const weeklyWorkload = [
        { day: 'Mon', hours: 8, tasks: 5 },
        { day: 'Tue', hours: 7.5, tasks: 4 },
        { day: 'Wed', hours: 9, tasks: 6 },
        { day: 'Thu', hours: 8, tasks: 5 },
        { day: 'Fri', hours: 6.5, tasks: 3 },
        { day: 'Sat', hours: 0, tasks: 0 },
        { day: 'Sun', hours: 0, tasks: 0 }
    ];

    const maxHours = Math.max(...weeklyWorkload.map(d => d.hours));

    return (
        <>
            {/* Profile Header with Summary Boxes */}
            <Row className="g-4 mb-4">
                {/* Left: Profile Information */}
                <Col lg={8}>
                    <div className="c-card-1 h-100">
                        <div className="c-card-body-1">
                            <Row className="g-4">
                                {/* Profile Image and Basic Info */}
                                <Col md={4}>
                                    <div className="text-center border-end">
                                        <div className="mb-3">
                                            <img 
                                                src={personData.avatar || 'https://randomuser.me/api/portraits/men/32.jpg'} 
                                                alt={personData.name}
                                                className="rounded-circle border-3 border-primary"
                                                style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                            />
                                        </div>
                                        <h5 className="mb-1 fw-bold text-dark">{personData.name}</h5>
                                        <div className="text-muted mb-3">{personData.role}</div>
                                        <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ backgroundColor: personData.status === 'Active' ? '#d1fae5' : '#e5e7eb' }}>
                                            <span className="d-inline-block rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: personData.status === 'Active' ? '#10b981' : '#6b7280' }}></span>
                                            <span className="small fw-semibold" style={{ color: personData.status === 'Active' ? '#10b981' : '#6b7280' }}>{personData.status}</span>
                                        </div>
                                    </div>
                                </Col>

                                {/* Contact and Employment Details */}
                                <Col md={8}>
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-building"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Department</div>
                                                    <div className="fw-semibold">{personData.department}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-briefcase"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Experience</div>
                                                    <div className="fw-semibold">{personData.experience}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 text-info flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-envelope"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Email</div>
                                                    <div className="fw-semibold text-truncate">{personData.email}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-phone"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Phone</div>
                                                    <div className="fw-semibold">{personData.phone}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-secondary bg-opacity-10 text-secondary flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-calendar"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Joining Date</div>
                                                    <div className="fw-semibold">{personData.joiningDate}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-user-tie"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Reporting TeamLead</div>
                                                    <div className="fw-semibold">{overviewData.reporting.teamLead.name}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px', backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                                                    <i className="fas fa-users"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Reporting Manager</div>
                                                    <div className="fw-semibold">{overviewData.reporting.manager.name}</div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={6}>
                                            <div className="d-flex align-items-start gap-2">
                                                <div className="d-flex align-items-center justify-content-center bg-info bg-opacity-10 text-info flex-shrink-0 rounded-2" style={{ width: '36px', height: '36px' }}>
                                                    <i className="fas fa-clock"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="text-muted small">Working Hours</div>
                                                    <div className="fw-semibold">{basicInfo.workingHours}</div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Right: Summary Boxes - Vertical Stack */}
                <Col lg={4}>
                    <Row className="g-3 h-100">
                        <Col sm={12}>
                            <div className="bg-white border rounded-3 p-3 summary-stat-card-hover">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning flex-shrink-0 rounded-3 icon-box-48">
                                        <i className="fas fa-tasks"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-warning fw-bold mb-1 stat-value-large">{overviewData.workload}%</div>
                                        <div className="text-secondary small">Current Workload</div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={12}>
                            <div className="bg-white border rounded-3 p-3 summary-stat-card-hover">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0 rounded-3 icon-box-48">
                                        <i className="fas fa-clock"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-primary fw-bold mb-1 stat-value-large">{overviewData.timeLogged.month}h</div>
                                        <div className="text-secondary small">Time Logged (Month)</div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={12}>
                            <div className="bg-white border rounded-3 p-3 summary-stat-card-hover">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0 rounded-3 icon-box-48">
                                        <i className="fas fa-project-diagram"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-success fw-bold mb-1 stat-value-large">{overviewData.projectsAssigned}</div>
                                        <div className="text-secondary small">Projects Assigned</div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Projects Assigned & Skill Matrix Row */}
            <Row className="g-4 mb-4">
                {/* Projects Assigned */}
                <Col lg={7}>
                    <div className="c-card-1 h-100">
                        <div className="c-card-header-1">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Projects Assigned</div>
                                <Link to={`/people/detail/${peopleId}/tasks`} className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md">View All Tasks </Link>
                                
                            </div>
                        </div>
                        <div className="c-card-body-1">
                            <Row className="g-3">
                                {projects.map(project => (
                                    <Col lg={6} key={project.id}>
                                        <div className="border rounded-3 p-3 h-100 project-card-hover">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div className="flex-grow-1 pe-2">
                                                    <h6 className="mb-1 fw-semibold text-dark">{project.name}</h6>
                                                    <div className="text-muted small mb-1">{project.client} </div>
                                                    <div className="text-muted small"> {project.role} </div>
                                                </div>
                                                <StatusBadge status={project.status} variant={project.color} className="flex-shrink-0" />
                                            </div>
                                            <div className="mt-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="small text-secondary fw-medium">Progress</span>
                                                    <span className="small fw-bold" style={{ color: project.progress >= 75 ? '#10b981' : project.progress >= 50 ? '#3b82f6' : '#f59e0b' }}>
                                                        {project.progress}%
                                                    </span>
                                                </div>
                                                <div className="progress" style={{ height: '8px' }}>
                                                    <div 
                                                        className="progress-bar"
                                                        role="progressbar" 
                                                        style={{ 
                                                            width: `${project.progress}%`,
                                                            backgroundColor: project.progress >= 75 ? '#10b981' : project.progress >= 50 ? '#3b82f6' : '#f59e0b'
                                                        }}
                                                        aria-valuenow={project.progress} 
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Skill Matrix */}
                <Col lg={5}>
                    <div className="c-card-1 h-100">
                        <div className="c-card-header-1">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div>Skill Matrix</div>
                                <Link to={`/people/skills`} className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md">View All Skill </Link>
                            </div>
                        </div>
                        <div className="c-card-body-1">
                            <Row className="g-3">
                                {skills.map((skill, index) => (
                                    <Col md={6} key={index}>
                                        <div className="border rounded-3 p-3 skill-card-hover h-100">
                                            <div className="d-flex align-items-center mb-2">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{ 
                                                        width: '32px', 
                                                        height: '32px', 
                                                        backgroundColor: `${skill.color}20`,
                                                        border: `1px solid ${skill.color}`
                                                    }}
                                                >
                                                    <i className={skill.icon} style={{ fontSize: '12px', color: skill.color }}></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-semibold small text-dark">{skill.name}</div>
                                                </div>
                                            </div>
                                            <div className="mt-2">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="small text-secondary">Proficiency</span>
                                                    <span className="small fw-bold" style={{ color: skill.color }}>{skill.level}%</span>
                                                </div>
                                                <div className="progress" style={{ height: '6px', backgroundColor: '#f3f4f6' }}>
                                                    <div 
                                                        className="progress-bar" 
                                                        role="progressbar" 
                                                        style={{ width: `${skill.level}%`, backgroundColor: `${skill.color}B3` }}
                                                        aria-valuenow={skill.level} 
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default PeopleOverview;
